import React, { useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import BulkAddToCartTable from "./BulkAddToCartTable";
import { useQuery } from "@apollo/client/react";

import { ASSETS_BY_IDS_WITH_QUANTITY_QUERY, SHOPPING_INVENTORY_CARTS_QUERY } from "../../../constants/graphql/queries";
import { ADD_SHOPPING_INVENTORY_ITEM_MUTATION } from "../../../constants/graphql/mutations";
import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import { useApolloClient } from "@apollo/client/react";

export default function BulkAddToCartModal({ selectedIds, onClose, onComplete }) {
  const { notifySuccess, notifyError } = useNotification();
  const apolloClient = useApolloClient();
  const [addInventoryItem] = useMutation(ADD_SHOPPING_INVENTORY_ITEM_MUTATION);
  const addInFlightRef = useRef(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentAsset: "" });
  const [assetStatus, setAssetStatus] = useState({});
  const [quantityOverrides, setQuantityOverrides] = useState({});

  const handleQuantityChange = (assetId, qty) =>
    setQuantityOverrides(prev => ({ ...prev, [assetId]: qty }));

  const getEffectiveQty = asset =>
    quantityOverrides[asset.id] !== undefined ? quantityOverrides[asset.id] : asset.availableQuantity;
  const { loading: assetsLoading, data: assetsData } = useQuery(ASSETS_BY_IDS_WITH_QUANTITY_QUERY, {
    variables: { assetIds: selectedIds },
    skip: !selectedIds || selectedIds.length === 0,
    fetchPolicy: "network-only",
  });

  const rawAssets = assetsData?.assetsCollection?.collection || [];
  const assets = rawAssets.filter(asset => asset.availableQuantity > 0);

  const handleAddToCart = async () => {
    if (addInFlightRef.current) return;

    if (!assets.length) {
      notifyError("No assets found");
      onClose();
      return;
    }

    addInFlightRef.current = true;
    setProcessing(true);
    setProgress({ current: 0, total: assets.length, currentAsset: "" });
    setAssetStatus({});

    const failed = [];
    for (let index = 0; index < assets.length; index++) {
      const asset = assets[index];
      setAssetStatus(prev => ({ ...prev, [asset.id]: "moving" }));
      setProgress({
        current: index + 1,
        total: assets.length,
        currentAsset: asset.assetId || asset.itemId || asset.id,
      });

      const qty = getEffectiveQty(asset);
      if (!qty || qty <= 0) {
        setAssetStatus(prev => ({ ...prev, [asset.id]: "failed" }));
        failed.push({ assetId: asset.assetId || asset.id, error: "No available quantity" });
        continue;
      }

      try {
        await addInventoryItem({
          variables: {
            assetId: asset.id,
            quantity: qty,
          },
        });
        setAssetStatus(prev => ({ ...prev, [asset.id]: "moved" }));
      } catch (mutationError) {
        setAssetStatus(prev => ({ ...prev, [asset.id]: "failed" }));
        failed.push({
          assetId: asset.assetId || asset.id,
          error: mutationError.message || "Unknown error",
        });
      }
    }

    try {
      apolloClient.refetchQueries({ include: [SHOPPING_INVENTORY_CARTS_QUERY] });
      if (failed.length === 0) {
        notifySuccess(`Successfully added ${assets.length} assets to the shopping cart`);
        window.dispatchEvent(new CustomEvent("openShoppingCart"));
        onComplete();
        onClose();
      } else {
        const successCount = assets.length - failed.length;
        if (successCount > 0) {
          notifySuccess(`Added ${successCount} assets. ${failed.length} failed.`);
          window.dispatchEvent(new CustomEvent("openShoppingCart"));
          onComplete();
        } else {
          notifyError("Failed to add any assets to the shopping cart");
        }
      }
    } finally {
      addInFlightRef.current = false;
      setProcessing(false);
    }
  };

  const totalAssets = assets.length || selectedIds.length;

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "center" }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography fontSize={20} fontWeight={500} color="textPrimary">
            Bulk Add to Shopping Cart
          </Typography>
          <Chip label={totalAssets} size="small" color="primary" variant="outlined" />
        </Box>
      </DialogTitle>

      <DialogContent>
        {assetsLoading && (
          <Box display="flex" flexDirection="column" alignItems="center" py={3}>
            <LinearProgress sx={{ width: "100%", mb: 2 }} />
            <Typography variant="body2" color="textSecondary">
              Fetching asset data for {totalAssets} selected assets...
            </Typography>
          </Box>
        )}

        {!assetsLoading && (
          <BulkAddToCartTable
            assets={assets}
            statusMap={assetStatus}
            quantityOverrides={quantityOverrides}
            onQuantityChange={handleQuantityChange}
            processing={processing}
          />
        )}
      </DialogContent>
      {processing && (
        <LinearProgress variant="determinate" value={(progress.current / progress.total) * 100} />
      )}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddToCart}
          disabled={processing || assetsLoading}
        >
          Add to Cart
        </Button>
      </DialogActions>
    </Dialog>
  );
}
